import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/20/solid';
import customFetch from '@utils/customFetch';
import NewCardSlideIn from '@components/dashboard/admin/kartenadministration/NewCardSlideIn';
import AdminListToolbar from '@components/dashboard/admin/shared/AdminListToolbar';
import Pagination from '@components/dashboard/admin/shared/Pagination';
import SortableHeader from '@components/dashboard/admin/shared/SortableHeader';
import StatusBadge, {
  statusFilterOptions,
} from '@components/dashboard/admin/shared/StatusBadge';
import useAdminListParams from '@/hooks/useAdminListParams';
import { formatDateTime } from '@utils/format';

// Query-Funktion mit URL-Parametern
const allCardsQuery = (searchParams) => ({
  queryKey: ['allCards', searchParams.toString()],
  queryFn: async () => {
    const { data } = await customFetch.get(`/cards?${searchParams.toString()}`);
    return data;
  },
});

export const loader =
  (queryClient) =>
  async ({ request }) => {
    const { searchParams } = new URL(request.url);
    return queryClient.ensureQueryData(allCardsQuery(searchParams));
  };

const COLUMN_COUNT = 6;

const CardList = () => {
  const { searchParams, page, limit, search, sort, getParam, setParams, setPage, reset } =
    useAdminListParams({ defaultSort: 'createdAt:desc' });
  const [showSlideIn, setShowSlideIn] = useState(false);

  const { data, isLoading, isError } = useQuery(allCardsQuery(searchParams));

  const cards = data?.cards ?? [];
  const totalCards = data?.totalCards ?? 0;
  const numOfPages = data?.numOfPages ?? 0;

  const handleSort = (value) => setParams({ sort: value });

  // Der Besitzerfilter kommt als Link aus der Benutzerverwaltung. Den Namen
  // dazu liefert die erste Karte — sonst stünde hier nur eine nackte ID.
  const ownerFilter = getParam('owner');
  const ownerOfFilter = ownerFilter ? cards[0]?.owner : null;

  return (
    <>
      <div className="bg-white px-6 py-6 sm:py-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">
              Kartenadministration
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {totalCards} Karte{totalCards === 1 ? '' : 'n'} gefunden.
            </p>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              type="button"
              className="block rounded-md bg-svisit-primary-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600"
              onClick={() => setShowSlideIn(true)}
            >
              Karte hinzufügen
            </button>
          </div>
        </div>

        <AdminListToolbar
          search={search}
          onSearchChange={(value) => setParams({ search: value })}
          searchPlaceholder="Kartenname, URL, Karten-ID, Besitzer …"
          filters={[
            {
              key: 'status',
              label: 'Status',
              value: getParam('status'),
              options: statusFilterOptions('card', 'Alle Stati'),
              onChange: (value) => setParams({ status: value }),
            },
          ]}
          onReset={reset}
        />

        {ownerFilter && (
          <div className="mt-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-svisit-primary-50 px-3 py-1 text-sm text-svisit-primary-700">
              Besitzer:{' '}
              {ownerOfFilter
                ? `${ownerOfFilter.firstName} ${ownerOfFilter.lastName}`
                : ownerFilter}
              <button
                type="button"
                onClick={() => setParams({ owner: '' })}
                className="rounded-full p-0.5 hover:bg-svisit-primary-100 hover:cursor-pointer"
              >
                <span className="sr-only">Besitzerfilter entfernen</span>
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </span>
          </div>
        )}

        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                    >
                      ID
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Besitzer
                    </th>
                    <SortableHeader
                      field="status"
                      label="Status"
                      sort={sort}
                      onSortChange={handleSort}
                    />
                    <SortableHeader
                      field="name"
                      label="Produkt"
                      sort={sort}
                      onSortChange={handleSort}
                    />
                    <SortableHeader
                      field="createdAt"
                      label="Hinzugefügt am"
                      sort={sort}
                      onSortChange={handleSort}
                    />
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-3"
                    >
                      <span className="sr-only">Bearbeiten</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {isLoading && (
                    <tr>
                      <td
                        colSpan={COLUMN_COUNT}
                        className="px-3 py-8 text-center text-sm text-gray-500"
                      >
                        Lädt …
                      </td>
                    </tr>
                  )}

                  {isError && !isLoading && (
                    <tr>
                      <td
                        colSpan={COLUMN_COUNT}
                        className="px-3 py-8 text-center text-sm text-red-600"
                      >
                        Karten konnten nicht geladen werden.
                      </td>
                    </tr>
                  )}

                  {!isLoading && !isError && cards.length === 0 && (
                    <tr>
                      <td
                        colSpan={COLUMN_COUNT}
                        className="px-3 py-8 text-center text-sm text-gray-500"
                      >
                        Keine Karten gefunden.
                      </td>
                    </tr>
                  )}

                  {cards.map((card) => (
                    <tr key={card._id} className="even:bg-gray-50">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                        {card._id}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {card.owner?._id ? (
                          <Link
                            to={`/dashboard/benutzerverwaltung/${card.owner._id}`}
                            className="text-svisit-primary-600 hover:text-svisit-primary-900"
                          >
                            {card.owner.firstName} {card.owner.lastName}
                            <span className="block text-xs text-gray-500">
                              {card.owner.email}
                            </span>
                          </Link>
                        ) : (
                          <span className="text-gray-400">
                            Kein Besitzer zugewiesen
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <StatusBadge type="card" status={card.status} />
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {card.name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {formatDateTime(card.createdAt)}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                        <Link
                          to={`/dashboard/kartenadministration/${card._id}`}
                          className="text-svisit-primary-600 hover:text-svisit-primary-900"
                        >
                          Bearbeiten
                          <span className="sr-only">, {card._id}</span>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination
                currentPage={page}
                numOfPages={numOfPages}
                total={totalCards}
                limit={limit}
                onPageChange={setPage}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Slide-In für neue Karte */}
      <NewCardSlideIn
        showSlideIn={showSlideIn}
        setShowSlideIn={setShowSlideIn}
      />
    </>
  );
};

export default CardList;
